import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';

const AddCategoryModal = ({
  open,
  onClose,
  onSubmit,
  title = 'Add New Category',
  placeholder = 'Enter category name',
  submitButtonText = 'Add Category'
}) => {
  const [categoryName, setCategoryName] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    if (categoryName.trim().length < 2) {
      setError('Category name must be at least 2 characters long');
      return;
    }

    onSubmit(categoryName.trim());
    setCategoryName('');
    setError('');
  };

  const handleClose = () => {
    setCategoryName('');
    setError('');
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />

      {/* Modal */}
      <div
        className="relative bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-xl w-full max-w-md mx-4 p-6"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-shoorah-primary dark:text-white">{title}</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="categoryName"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Category Name <span className="text-red-500">*</span>
            </label>
            <input
              ref={inputRef}
              type="text"
              id="categoryName"
              value={categoryName}
              onChange={(e) => {
                setCategoryName(e.target.value);
                setError('');
              }}
              placeholder={placeholder}
              className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:border-shoorah-primary dark:text-white"
              maxLength={50}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            <p className="text-gray-500 text-sm mt-1">{categoryName.length}/50 characters</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!categoryName.trim()}
              className="btn-primary flex-1"
            >
              {submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

AddCategoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  submitButtonText: PropTypes.string
};

export default AddCategoryModal;
